import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getLeaderboard, getBadges } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, Medal, Award, Star, Loader2 } from 'lucide-react';

const RANK_STYLES = {
  1: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Trophy },
  2: { bg: 'bg-slate-100', text: 'text-slate-700', icon: Medal },
  3: { bg: 'bg-orange-100', text: 'text-orange-700', icon: Award },
};

export default function Leaderboard() {
  const [leaders, setLeaders] = useState([]);
  const [badges, setBadges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [leadersRes, badgesRes] = await Promise.all([
        getLeaderboard({ limit: 20 }),
        getBadges()
      ]);
      setLeaders(leadersRes.data);
      setBadges(badgesRes.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="leaderboard-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Leaderboard</h1>
          <p className="text-slate-600 mt-1">Top contributors in the PDX Hacks community</p>
        </div>

        <Tabs defaultValue="reputation" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="reputation">Top Contributors</TabsTrigger>
            <TabsTrigger value="badges">Badges</TabsTrigger>
          </TabsList>

          <TabsContent value="reputation">
            {/* Top 3 Podium */}
            {leaders.length >= 3 && (
              <div className="grid grid-cols-3 gap-4 mb-8">
                {[1, 0, 2].map((index) => {
                  const leader = leaders[index];
                  if (!leader) return null;
                  const rank = index === 1 ? 1 : index === 0 ? 2 : 3;
                  const style = RANK_STYLES[rank];
                  const RankIcon = style.icon;
                  
                  return (
                    <div 
                      key={leader.user_id}
                      className={`${rank === 1 ? 'order-2 -mt-4' : rank === 2 ? 'order-1 mt-4' : 'order-3 mt-4'}`}
                    >
                      <Card className={`text-center ${rank === 1 ? 'border-amber-300 shadow-lg' : ''}`}>
                        <CardContent className="pt-6">
                          <div className={`w-12 h-12 mx-auto mb-3 ${style.bg} rounded-full flex items-center justify-center`}>
                            <RankIcon className={`w-6 h-6 ${style.text}`} />
                          </div>
                          <Avatar className="w-16 h-16 mx-auto mb-3">
                            <AvatarImage src={leader.picture} />
                            <AvatarFallback className="text-xl bg-indigo-100 text-indigo-700">
                              {leader.name?.charAt(0)?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <Link to={`/users/${leader.user_id}`}>
                            <h3 className="font-semibold text-slate-900 hover:text-indigo-600">
                              {leader.name}
                            </h3>
                          </Link>
                          <p className="text-2xl font-bold text-indigo-600 mt-1">
                            {leader.reputation}
                          </p>
                          <p className="text-xs text-slate-500">reputation</p>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rest of the list */}
            <Card>
              <CardContent className="divide-y divide-slate-100">
                {leaders.slice(3).map((leader, index) => (
                  <div key={leader.user_id} className="flex items-center gap-4 py-4">
                    <div className="w-8 text-center font-bold text-slate-400">
                      #{index + 4}
                    </div>
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={leader.picture} />
                      <AvatarFallback className="bg-indigo-100 text-indigo-700">
                        {leader.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <Link to={`/users/${leader.user_id}`}>
                        <h4 className="font-medium text-slate-900 hover:text-indigo-600">
                          {leader.name}
                        </h4>
                      </Link>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="secondary" 
                          className="text-xs"
                          style={{ backgroundColor: `var(--${leader.level?.color}-100)`, color: `var(--${leader.level?.color}-700)` }}
                        >
                          {leader.level?.name}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-indigo-600">{leader.reputation}</p>
                      <p className="text-xs text-slate-500">pts</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="badges">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {badges.map((badge) => (
                <Card key={badge.id}>
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 bg-indigo-100 rounded-full flex items-center justify-center">
                      <Star className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900">{badge.name}</h4>
                    <p className="text-sm text-slate-600 mt-1">{badge.description}</p>
                    <Badge variant="secondary" className="mt-2">
                      +{badge.points} pts
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
